import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "data-visualization",
  name: "データ可視化",
  nameEn: "Data Visualization",
  seo: {
    title:
      "データ可視化を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Matplotlib・Plotly・ダッシュボード構築からBIツール連携まで、データ可視化スキルをゆめさくエンジニアで実践的に学びましょう。データを「伝わる形」にするスキルはあらゆる職種で重宝されます。",
    url: "/columns/data-visualization-programming-school",
  },
  hero: {
    title: "データ可視化を学べる女性向けプログラミングスクール",
    englishTitle: "Learn Data Visualization",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "データ可視化" },
  ],
  overview: {
    heading: "データ可視化とは？",
    paragraphs: [
      "データ可視化とは、数値や統計データをグラフ・チャート・ダッシュボードなどのビジュアル表現に変換し、データの傾向や洞察を直感的に理解できるようにする技術です。",
      "ビジネスの意思決定においてデータドリブンなアプローチが当たり前になった現在、膨大なデータを「伝わる形」に変換できるスキルは非常に高く評価されています。Excelのグラフだけでなく、Matplotlib・Plotly・Tableauなどの専門ツールを使いこなすことで、よりインパクトのあるデータストーリーテリングが可能になります。",
      "データ可視化はデータ分析の最終アウトプットであり、経営層への報告、マーケティング施策の評価、プロダクト改善の根拠提示など、あらゆるビジネスシーンで活用されます。",
    ],
    useCases: [
      {
        label: "ダッシュボード構築",
        description:
          "売上推移・KPI・ユーザー行動などをリアルタイムに可視化するダッシュボードを構築し、チーム全体でデータを共有します。",
      },
      {
        label: "レポート・プレゼン資料",
        description:
          "経営会議やクライアント向けプレゼンで、データの傾向やインサイトを効果的に伝えるビジュアルレポートを作成します。",
      },
      {
        label: "データ探索・EDA",
        description:
          "機械学習やデータ分析の前段階として、データの分布・相関・外れ値を可視化し、データの特性を把握します。",
      },
      {
        label: "BIツール連携",
        description:
          "Tableau・Looker・Power BIなどのBIツールと連携し、非エンジニアでも活用できるセルフサービス分析環境を構築します。",
      },
    ],
  },
  whyLearn: {
    heading: "データ可視化を学ぶべき3つの理由",
    items: [
      {
        title: "データドリブン経営の浸透で需要が急増",
        description:
          "DX推進により、あらゆる企業がデータに基づく意思決定を重視しています。データを分かりやすく伝えられる人材は、エンジニア・非エンジニア問わず高い市場価値を持ちます。",
      },
      {
        title: "技術スキルとコミュニケーション力の両立",
        description:
          "データ可視化は「技術力」と「伝える力」を同時に鍛えられる分野です。コードでグラフを作る技術力に加え、相手に合わせた表現を選ぶコミュニケーション力も身につきます。",
      },
      {
        title: "データ分析・AI分野へのステップアップに直結",
        description:
          "データ可視化のスキルは、データアナリスト・データサイエンティスト・BIエンジニアなど、高年収が期待できるキャリアへの足がかりとなります。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでデータ可視化を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Pythonの基礎を習得した後、上級Phaseでデータ可視化の実践スキルを磨きます。Matplotlib・seabornによる基本的なグラフ作成からPlotlyによるインタラクティブな可視化、さらにダッシュボード構築まで段階的に学びます。",
      "実際のビジネスデータセットを使い、「伝わるグラフ」のデザイン原則やBIツールとの連携方法も習得できます。",
    ],
    topics: [
      "Matplotlib・seabornによる基本グラフ作成",
      "Plotlyによるインタラクティブ可視化",
      "Streamlit・Dashによるダッシュボード構築",
      "データストーリーテリングの原則",
      "BIツール（Tableau・Looker）連携の基礎",
      "pandas DataFrameからの可視化パイプライン",
      "実践プロジェクト：経営ダッシュボードの構築",
    ],
  },
  careerPaths: {
    heading: "データ可視化を活かせるキャリア",
    items: [
      {
        title: "データアナリスト",
        description:
          "ビジネスデータを収集・分析し、可視化を通じて意思決定を支援する職種です。マーケティング・経営企画・プロダクト改善など幅広い部門で活躍できます。",
        salary: "450〜750万円",
      },
      {
        title: "BIエンジニア",
        description:
          "Tableau・Looker・Power BIなどのBIツールを活用し、企業のデータ分析基盤やダッシュボードを設計・構築する専門職です。",
        salary: "500〜800万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜10ヶ月（Python基礎+可視化ライブラリ+BIツール）",
        luagate: "3〜4ヶ月（実務で使える可視化スキルを効率的に習得）",
      },
      {
        label: "挫折率",
        selfStudy: "「グラフは作れるが伝わらない」壁で挫折しやすい",
        luagate: "デザイン原則とフィードバックで「伝わる可視化」が身につく",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "サンプルデータでの練習にとどまりがち",
        luagate: "実際のビジネスデータでダッシュボードを構築",
      },
      {
        label: "質問対応",
        selfStudy: "グラフの選び方やデザインに迷いやすい",
        luagate: "メンターが最適な可視化手法を一緒に検討",
      },
      {
        label: "就職サポート",
        selfStudy: "ポートフォリオの見せ方がわからない",
        luagate: "ダッシュボード作品を含む実践的なポートフォリオ作成を支援",
      },
    ],
  },
  faq: [
    {
      question: "データ可視化を学ぶのにデザインセンスは必要ですか？",
      answer:
        "デザインセンスよりも「データの読み方」と「伝える原則」が重要です。ゆめさくエンジニアでは情報デザインの基本原則を体系的に学ぶため、デザイン未経験の方でも効果的な可視化を作れるようになります。",
    },
    {
      question: "ExcelやGoogleスプレッドシートのグラフとの違いは何ですか？",
      answer:
        "Excelのグラフは手軽ですが、大量データの処理やインタラクティブな表現には限界があります。Python（Plotly等）やBIツールを使うことで、リアルタイム更新・フィルタリング・ドリルダウンなど高度な分析が可能になります。",
    },
    {
      question: "データ可視化だけでキャリアを築けますか？",
      answer:
        "データ可視化はデータアナリスト・BIエンジニアの中核スキルですが、SQL・統計・ビジネスドメイン知識と組み合わせることでより高い市場価値を持てます。ゆめさくエンジニアではこれらを総合的に学べるカリキュラムを提供しています。",
    },
    {
      question: "BIツール（Tableau等）のライセンス費用は必要ですか？",
      answer:
        "学習段階ではTableau Publicや各ツールの無料版が利用できます。ゆめさくエンジニアではPythonベースの可視化を中心に学びつつ、BIツールの基礎も無料版で体験できるカリキュラムを提供しています。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "sql", name: "SQL" },
    { slug: "ai-ml", name: "AI・機械学習" },
    { slug: "ml-dl", name: "ML/DL応用" },
  ],
};
